import { createStart } from '@tanstack/react-start';
import { setVibeWsTokenFetcher } from '@vibe/db/client';
import { ensureVibeGuest } from '@api/ensure-vibe-guest';
import { getVibeWsToken } from '@api/vibe-ws-token';
import { APP_REQUEST_HEADER, APP_REQUEST_HEADER_VALUE } from '@lib/vibe-app-request';
import { getAuthToken, installVibeAuthBridge, VIBE_SERVER_URL } from '@lib/vibe-auth';
import { getStoredGuestToken, setStoredGuestToken } from '@lib/vibe-guest';

// Shared client+server Start configuration.
//
//

const isBrowser = typeof window !== 'undefined';

if (isBrowser) {
  installVibeAuthBridge();
  // The realtime socket mints its WS token through the app's own Lambda (this server fn), never the
  // builder backend — same origin + auth as every other server-fn call.
  setVibeWsTokenFetcher(() => getVibeWsToken());
  // Public/external apps: establish a DO-issued guest identity before realtime/writes need it, and
  // persist the returned opaque token. Members (private/internal) get { guestToken: null } and skip.
  // ponytail: fire-and-persist; a write/WS-mint in the brief window before this resolves is simply
  // unidentified (actor falls back to 'service' / socket retries) — no incorrect attribution.
  ensureVibeGuest()
    .then((r) => {
      if (r.guestToken) setStoredGuestToken(r.guestToken);
    })
    .catch(() => {
      // transient — the next server-fn round-trip re-establishes it
    });
}

function toAbsoluteServerUrl(input: RequestInfo | URL): RequestInfo | URL {
  if (typeof input === 'string' && input.startsWith('/')) {
    return `${VIBE_SERVER_URL}${input}`;
  }
  if (input instanceof URL && input.origin === window.location.origin) {
    return `${VIBE_SERVER_URL}${input.pathname}${input.search}`;
  }
  return input;
}

export const startInstance = createStart(() => ({
  requestMiddleware: [],
  serverFns: {
    fetch: isBrowser
      ? (input: RequestInfo | URL, init?: RequestInit) => {
          const token = getAuthToken();
          const headers = new Headers(init?.headers);
          if (!VIBE_SERVER_URL) {
            headers.set(APP_REQUEST_HEADER, APP_REQUEST_HEADER_VALUE);
          }
          if (token) {
            headers.set('Authorization', `Bearer ${token}`);
          }
          // Public/external apps have no monday bearer: attach the browser's opaque DO-issued guest
          // token so the server resolves the anonymous viewer's DO-owned id. Harmless in private
          // mode (server ignores it). Empty on the very first load until ensureVibeGuest() resolves.
          const guestToken = getStoredGuestToken();
          if (guestToken) {
            headers.set('x-vibe-guest-token', guestToken);
          }
          return fetch(toAbsoluteServerUrl(input), { ...init, headers });
        }
      : undefined,
  },
}));
